import { useState, useEffect, useMemo, useCallback } from 'react'
import { Helmet } from 'react-helmet-async'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  type SortingState,
  type ColumnDef,
} from '@tanstack/react-table'
import { getRaces, getAwards } from '../api/races'
import { useCrossAuth } from '../hooks/useCrossAuth'
import type { RaceSummary, MemberRace, Award } from '../types'

const currentYear = new Date().getFullYear()

function buildSeasonOptions() {
  const options: { value: string; label: string }[] = []
  for (let y = currentYear; y >= currentYear - 5; y--) {
    options.push({ value: `${y}ob`, label: `${y} Old Bird` })
    options.push({ value: `${y}yb`, label: `${y} Young Bird` })
  }
  return options
}

const SEASON_OPTIONS = buildSeasonOptions()

export function Races() {
  const [season, setSeason] = useState(`${currentYear}ob`)
  const [summary, setSummary] = useState<RaceSummary | null>(null)
  const [races, setRaces] = useState<MemberRace[]>([])
  const [awards, setAwards] = useState<Award[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [sorting, setSorting] = useState<SortingState>([])
  const { getCrossAuthUrl } = useCrossAuth()

  const handleNdbClick = useCallback(async (e: React.MouseEvent, ndbLink: string) => {
    e.preventDefault()
    const url = await getCrossAuthUrl(ndbLink)
    window.open(url, '_blank', 'noopener,noreferrer')
  }, [getCrossAuthUrl])

  useEffect(() => {
    setLoading(true)
    setError('')
    getRaces(season)
      .then((data) => {
        setSummary(data.summary)
        setRaces(data.races)
      })
      .catch(() => setError('Failed to load race results'))
      .finally(() => setLoading(false))
  }, [season])

  useEffect(() => {
    getAwards()
      .then((data) => setAwards(data.awards))
      .catch(() => { /* awards are optional, don't block */ })
  }, [])

  const columns = useMemo<ColumnDef<MemberRace>[]>(() => [
    {
      accessorKey: 'raceName',
      header: 'Race Name',
      cell: ({ row }) => (
        <a
          href={row.original.ndbLink}
          onClick={(e) => handleNdbClick(e, row.original.ndbLink)}
          className="text-primary hover:underline cursor-pointer"
        >
          {row.original.raceName}
        </a>
      ),
    },
    {
      accessorKey: 'date',
      header: 'Date',
    },
    {
      accessorKey: 'organization',
      header: 'Organization',
    },
    {
      accessorKey: 'bestPosition',
      header: 'Speed Rank',
      cell: ({ row, getValue }) => {
        const pos = getValue<number | null>()
        const field = row.original.fieldSize
        return pos != null ? `#${pos}${field ? ` of ${field}` : ''}` : '-'
      },
    },
    {
      accessorKey: 'speed',
      header: 'Speed (YPM)',
      cell: ({ getValue }) => {
        const speed = getValue<number | null>()
        return speed != null ? speed.toFixed(2) : '-'
      },
    },
    {
      accessorKey: 'birdBandNo',
      header: 'Band #',
    },
  ], [handleNdbClick])

  const table = useReactTable({
    data: races,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  const awardBadges = useMemo(() => {
    const types = new Map<string, number>()
    for (const a of awards) {
      types.set(a.type, (types.get(a.type) ?? 0) + 1)
    }
    return types
  }, [awards])

  return (
    <>
      <Helmet><title>My Races - ARPU</title></Helmet>
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <h1 className="text-2xl font-heading font-bold text-text">My Races</h1>
          <select
            value={season}
            onChange={(e) => setSeason(e.target.value)}
            className="px-3 py-2 border border-border rounded-md bg-surface text-text focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary text-sm"
          >
            {SEASON_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Summary cards */}
        {summary && !loading && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <SummaryCard label="Total Races" value={summary.totalRaces} />
            <SummaryCard label="Best Finish" value={summary.bestFinish != null ? `#${summary.bestFinish}` : '-'} />
            <SummaryCard label="Birds Raced" value={summary.totalBirds} />
            <SummaryCard label="Awards" value={awards.length} />
          </div>
        )}

        {/* Awards badges */}
        {awards.length > 0 && (
          <div className="flex flex-wrap gap-3 mb-6">
            {awardBadges.has('hof') && (
              <AwardBadge label="Hall of Fame" count={awardBadges.get('hof')!} color="bg-accent/10 text-accent border-accent/30" />
            )}
            {awardBadges.has('champion_loft') && (
              <AwardBadge label="Champion Loft" count={awardBadges.get('champion_loft')!} color="bg-primary/10 text-primary border-primary/30" />
            )}
            {awardBadges.has('ace_pigeon') && (
              <AwardBadge label="Ace Pigeon" count={awardBadges.get('ace_pigeon')!} color="bg-success/10 text-success border-success/30" />
            )}
            {awardBadges.has('registered_champion') && (
              <AwardBadge label="Registered Champion" count={awardBadges.get('registered_champion')!} color="bg-secondary/10 text-secondary border-secondary/30" />
            )}
          </div>
        )}

        {error && (
          <div className="bg-error/10 border border-error/30 text-error rounded-md px-4 py-3 mb-4 text-sm">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
          </div>
        ) : races.length === 0 ? (
          <div className="text-center py-12 text-text-muted">
            No race results found for this season.
          </div>
        ) : (
          <div className="border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-surface-alt">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          onClick={header.column.getToggleSortingHandler()}
                          className="text-left px-4 py-2.5 font-medium text-text-muted cursor-pointer select-none hover:text-text"
                        >
                          <span className="flex items-center gap-1">
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {{
                              asc: ' ^',
                              desc: ' v',
                            }[header.column.getIsSorted() as string] ?? ''}
                          </span>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map((row) => (
                    <tr key={row.id} className="border-t border-border hover:bg-surface-hover transition-colors">
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className="px-4 py-2 text-text">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

function SummaryCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="bg-surface border border-border rounded-lg p-4 text-center">
      <div className="text-2xl font-bold text-text">{value}</div>
      <div className="text-xs text-text-muted mt-1">{label}</div>
    </div>
  )
}

function AwardBadge({ label, count, color }: { label: string; count: number; color: string }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium ${color}`}>
      {label}
      {count > 1 && <span className="opacity-70">x{count}</span>}
    </span>
  )
}
